// The addresses typed at this stand that nobody ever proved.
//
// ---------------------------------------------------------------------------
// THE TALLY IS THE ANSWER AND THE ADDRESSES ARE THE EVIDENCE, IN THAT ORDER
//
// xp/operator/src/unverifiedAttempts.ts states the ruling this layout exists to
// honour: the feature was asked for so that drop-off could be analysed, the
// worked example was eleven people at one stand typing `gmial.com`, and that
// question is answerable without reading a single address. The service puts
// `domains` above `attempts` in its own type so that the cheaper answer is the
// one a screen reaches for first, and this screen draws them in that order:
// the tally is counted over the whole activation and sits directly under the
// heading, the addresses are further down and behind a control.
//
// The tally is also the half that does not move. It is computed in the database
// over every unproved address here, so paging changes the evidence and never
// the finding. A tally computed in a browser over the fifty rows somebody
// happened to fetch would answer the question wrongly in a way nobody would
// notice, because eleven of anything looks plausible.
//
// ---------------------------------------------------------------------------
// THE ONE FAILURE MODE THIS READ HAS, AND WHAT IS NOT ON THIS SCREEN BECAUSE
// OF IT
//
// The operator module names it: somebody copying a column of these into a
// mailing tool. So there is no copy-all, no select-all, no export, no checkbox
// column and no per-row copy control. That is not an oversight to be filled in
// later by somebody who thinks a list without a copy button is unfinished.
//
// Nothing here sorts or filters either, and the second of those is worth its
// own sentence because People has a search box. There the filter narrows a page
// of an activation's own registered attendees, and Choose.tsx's rule covers it:
// a filter over rows already delivered discloses nothing that was not already
// on the screen. Here the rows are strangers' mailboxes and the useful thing to
// search for is a domain, which is the tally, already drawn, over the whole
// activation rather than over the page. A search box would be a worse version
// of the section above it that also taught the reader to treat this table as
// something to look people up in.
//
// ---------------------------------------------------------------------------
// THE DISCLOSURE CONTROL IS ABOUT THE SCREEN AND NOT ABOUT THE DISCLOSURE
//
// The addresses arrive in the same response as the tally. There is no request
// that fetches one without the other, the `report_access` row is written by the
// service before the answer leaves it, and its `row_count` is the number of
// addresses this page carried whether or not any of them was drawn. So pressing
// Hide does not unsend anything and does not remove the record.
//
// What the control is actually for is narrower and still worth having: a
// console is read at a desk with people walking past it, and somebody who came
// to this screen for the domain tally should not have a column of strangers'
// mailboxes on their monitor while they read it. The copy beside the button
// says exactly that, in those terms, because a control that let somebody
// believe they had limited a disclosure would be worse than no control at all.
//
// ---------------------------------------------------------------------------
// A CLIENT VIEWER IS REFUSED HERE, WHICH IS A DEPARTURE AND IS ARGUED RATHER
// THAN ASSUMED
//
// Since 12 Oct every route asks roleAccess.ts what it opens, and the map is tied
// to the service's guards by check.mjs. This screen is the one that draws its
// own refusal rather than the router's, the design's (console-live.jsx), and
// it asks the same map before it mounts its read. The refusal it draws is its
// own rather than the service's, for three reasons:
//
//   The rule has no inputs. `requireService` in the operator module accepts
//   `kind === "service"` and refuses everything else. It is not a grant check,
//   so unlike every rule this console is forbidden from copying, there is no
//   configuration in which a client viewer is allowed and a local refusal would
//   be wrong. The copy cannot drift from the original because the original has
//   nothing to drift over.
//
//   The service's sentence is knowingly the wrong one. reports/routes.ts
//   refuses this endpoint with `attendeesAreNovaOnly`, which sends the attendee
//   list's wording, and its own comment calls that "a real mismatch and the
//   lesser of the two available wrongs" rather than mint a fourth code for one
//   rule. A client viewer walked into that refusal is told they may not read
//   the people at this activation, which is true and is not what they asked
//   for. The sentence below is about this read: unproved addresses, some of
//   them strangers'. That comment also names the console as the reader of those
//   codes, so this is the console doing the part it was left.
//
//   And the service itself keeps three layers for this one rule, on purpose,
//   with the argument in migration 130's grant section: this is the read where
//   the case for a client seeing their own stand's data is strongest and must
//   still lose. A fourth layer in front of the other three is in the spirit of
//   that rather than against it.
//
// It is refused in two places, and neither of them is the route. The rail does
// not advertise the screen to a client viewer, which is the ordinary filter
// Data, Ride, Show details and People all use. This component refuses one, so a
// deep link is answered rather than followed. The route stays reachable and no
// request is made.

import { DefList, NoticeBox, PageHead } from "@nova/desk-ui/parts";
import { Empty, SecLabel } from "@nova/desk-ui/kit";
import { Pager } from "@nova/desk-ui/metrics";
import { DeskPage, SortHead, useListSort, type ListSort } from "@nova/desk-ui/page";
import { ListState, Loading } from "@nova/desk-ui/states";
import { useCallback, useEffect, useState, type ReactNode } from "react";
import type { UnverifiedDomainSortKey, UnverifiedSortKey } from "../../../operator/src/unverifiedAttempts.js";
import {
  readUnverifiedAttempts,
  UNVERIFIED_PER_PAGE,
  type UnverifiedAttempt,
  type UnverifiedAttemptPage,
  type UnverifiedDomain,
  type UnverifiedRetention,
} from "../api/unverifiedAttempts.js";
import type { ApiOptions, Failure } from "../api/client.js";
import type { SessionToken } from "../session/store.js";
import { ReadFailed } from "./ReadFailed.js";
import { opens } from "./roleAccess.js";
import { useShow } from "./Show.js";

export type UnverifiedProps = {
  readonly api: ApiOptions;
  readonly token: SessionToken;
  readonly activationId: string;
  readonly onSessionEnded: (detail: string) => void;
  readonly role: string;
  readonly onEnrol: () => void;
  /** The other half of the same question: the people who did finish. */
  readonly onPeople: () => void;
};

/**
 * What this screen is, before anything is read.
 *
 * The second sentence is People's, deliberately word for word, because it is
 * the same fact about a heavier read and two screens that said it differently
 * would invite a reader to work out which one was the serious one.
 */
const LEDE =
  "Email addresses that asked for a code but were never verified. Opening this page is recorded.";

type Paged =
  | { readonly status: "reading" }
  | { readonly status: "read" }
  | { readonly status: "failed"; readonly failure: Failure };

/** The addresses at rest: newest attempt first, the service's own order and true of Last tried. */
const ADDRESSES_REST: ListSort<UnverifiedSortKey> = { key: "last", dir: "desc" };
const ADDRESSES_FIRST: Readonly<Partial<Record<UnverifiedSortKey, "asc" | "desc">>> = {
  first: "desc",
  last: "desc",
  codes: "desc",
};
/** The tally at rest: most addresses first, the service's own order and true of Addresses. */
const DOMAINS_REST: ListSort<UnverifiedDomainSortKey> = { key: "addresses", dir: "desc" };
const DOMAINS_FIRST: Readonly<Partial<Record<UnverifiedDomainSortKey, "asc" | "desc">>> = {
  addresses: "desc",
  codes: "desc",
};

type Heads<K extends string> = {
  readonly sort: ListSort<K>;
  readonly lit: boolean;
  readonly onSort: (key: K) => void;
};

export function Unverified({
  api,
  token,
  activationId,
  onSessionEnded,
  role,
  onEnrol,
  onPeople,
}: UnverifiedProps): ReactNode {
  // BEFORE THE COMPONENT THAT HOLDS THE EFFECT, so that a browser the service
  // would refuse never mounts the thing that would ask. A guard inside the
  // reading component would be one edit away from a request that files nothing
  // and still leaves a log line naming this activation and that credential.
  // The router already closes this page to anybody roleAccess.ts does not give
  // `unverified` (an Admin's: the service refuses an Editor by requireService
  // and a Viewer by attendeesAreNovaOnly); this asks the same map, so the
  // screen is safe mounted anywhere without holding a second copy of the rule.
  if (!opens({ role, canExport: null }, "unverified")) return <NotThisSignIn />;

  return (
    <Read
      api={api}
      token={token}
      activationId={activationId}
      onSessionEnded={onSessionEnded}
      role={role}
      onEnrol={onEnrol}
      onPeople={onPeople}
    />
  );
}

/**
 * The refusal a client viewer meets instead of the screen.
 *
 * NOT `Refusal`, THOUGH IT WEARS THE SAME FURNITURE AND THE SAME HEADING. That
 * component draws a `Failure`, which is a decision a service made about a
 * request that was sent. Nothing was sent here, and dressing this up as one
 * would put a sentence the service never wrote inside the box this console uses
 * for the service's own words. The heading is borrowed on purpose so that the
 * two do not look like different kinds of thing to somebody who meets both.
 *
 * No enrolment offer, and that is the same call `Refusal` makes: an
 * authenticator is what an unstamped nova_admin is missing, and a client viewer
 * who enrolled one would still be refused. Offering it would read as an
 * instruction that fixes this.
 */
function NotThisSignIn(): ReactNode {
  return (
    <div className="stack">
      <header className="head">
        <h1>This page is for Nova admins</h1>
      </header>
      <NoticeBox kind="bad" title="You do not have access" alert>
        <p>
          These email addresses were never verified. Some may be mistyped or belong to people who
          never visited the stand.
        </p>
      </NoticeBox>
      <p className="quiet">Nothing was loaded. Ask Nova for a domain summary if you need one.</p>
    </div>
  );
}

function Read({
  api,
  token,
  activationId,
  onSessionEnded,
  role,
  onEnrol,
  onPeople,
}: UnverifiedProps): ReactNode {
  const show = useShow();
  const [paged, setPaged] = useState<Paged>({ status: "reading" });
  // Bumped by "Ask again"; the read below runs again whenever it changes.
  const [attempt, setAttempt] = useState(0);
  /**
   * The last page the service handed over.
   *
   * KEPT ACROSS A PAGE TURN, WHICH IS WHAT KEEPS THE NOTES ON THE SCREEN, and
   * People.tsx carries the general form of this argument. It binds harder here
   * because of which sentence would go: `use_note` is the one saying these
   * mailboxes are unproved, and a frame that unmounted it while the next page
   * of addresses was fetched would take the warning off the screen at the exact
   * moment a fresh column of addresses arrived under where it had been.
   */
  const [held, setHeld] = useState<UnverifiedAttemptPage | null>(null);
  const [per, setPer] = useState(UNVERIFIED_PER_PAGE);
  const [page, setPage] = useState(1);
  /**
   * Whether the addresses are drawn. Not whether they were sent.
   *
   * Starts closed, which is a decision about the first render rather than about
   * disclosure: somebody arriving from the rail or from People is here for the
   * tally, and the addresses are what they open when the tally has told them
   * which domain to look at. The header has the whole argument, and the copy
   * beside the control says the same thing to the reader.
   */
  const [showing, setShowing] = useState(false);
  // BOTH ORDERS ARE THE SERVICE'S, ASKED FOR AND NOT APPLIED HERE (12 Oct
  // hand-off: every column sorts, on the server). The addresses are paged, so
  // a browser sort would order one page of them; the tally is counted over the
  // whole activation and ordered where it is counted.
  const addresses = useListSort<UnverifiedSortKey>(ADDRESSES_REST, ADDRESSES_FIRST);
  const domainsOrder = useListSort<UnverifiedDomainSortKey>(DOMAINS_REST, DOMAINS_FIRST);
  const addressKey = addresses.lit ? addresses.sort.key : null;
  const addressDir = addresses.lit ? addresses.sort.dir : null;
  const domainKey = domainsOrder.lit ? domainsOrder.sort.key : null;
  const domainDir = domainsOrder.lit ? domainsOrder.sort.dir : null;
  const { onSort: pressAddresses } = addresses;
  // A new order of the addresses starts at its own first page.
  const sortAddresses = useCallback(
    (key: UnverifiedSortKey) => {
      pressAddresses(key);
      setPage(() => 1);
    },
    [pressAddresses],
  );

  useEffect(() => {
    // The frame's directory read goes first, which is People's rule and
    // Ride.tsx's before it: two reads started in one tick arrive in whatever
    // order the network settles them, and this one hands over addresses.
    if (show.status !== "ready") return;

    let cancelled = false;
    setPaged({ status: "reading" });

    void (async () => {
      const result = await readUnverifiedAttempts(
        api,
        token,
        activationId,
        (page - 1) * per,
        per,
        addressKey === null || addressDir === null ? null : { key: addressKey, dir: addressDir },
        domainKey === null || domainDir === null ? null : { key: domainKey, dir: domainDir },
      );
      if (cancelled) return;
      if (!result.ok) {
        setPaged({ status: "failed", failure: result });
        // A 401 is the one refusal that ends a session. A 403 does not, and
        // signing somebody out for asking to read something they may not read
        // is the mistake client.ts names.
        if (result.kind === "session_ended") onSessionEnded(result.detail);
        return;
      }
      setHeld(result.value);
      setPaged({ status: "read" });
    })();

    return () => {
      cancelled = true;
    };
  }, [api, token, activationId, onSessionEnded, show.status, page, per, attempt, addressKey, addressDir, domainKey, domainDir]);

  // THE 12 OCTOBER PAGE SHAPE, FOR A PAGE WITH NOTHING TO FILTER. No search
  // and no filter, so no right-hand column: the page is 1280px and centred,
  // and the addresses' pager is DeskPage's slot, under the addresses when they
  // are drawn and nowhere when they are not.
  const head = <PageHead title="Unverified emails" lede={LEDE} uuid={activationId} explain="info" />;

  // A FAILED READ IS DRAWN UNDER THE PAGE'S OWN HEAD, as the design draws it
  // (console-live.jsx: `{head}<Failed what="Could not load unverified emails"`),
  // with Ask again. It used to replace the head with a refusal of its own.
  const failed =
    show.status === "failed"
      ? {
          failure: show.failure,
          retry: show.reload,
          notCounted: "No totals are shown because the activation did not load.",
        }
      : paged.status === "failed"
        ? {
            failure: paged.failure,
            retry: () => setAttempt((n) => n + 1),
            notCounted: "No totals are shown because this page did not load.",
          }
        : null;

  if (failed !== null) {
    return (
      <DeskPage head={head}>
        <ReadFailed
          what="Could not load unverified emails"
          failure={failed.failure}
          role={role}
          onEnrol={onEnrol}
          notCounted={failed.notCounted}
          onRetry={failed.retry}
        />
      </DeskPage>
    );
  }

  if (held === null) {
    return (
      <DeskPage head={head}>
        <section>
          <SecLabel>Where the addresses went</SecLabel>
          {/* Three columns because that is what the tally below has. A skeleton
              with the wrong number of them is the jump it exists to prevent. */}
          <Loading rows={4} cols={3} label="Loading domains…" />
        </section>
      </DeskPage>
    );
  }

  const { domains, attempts, retention } = held;

  return (
    <DeskPage
      head={head}
      // THE PAGER TURNS THE SERVICE'S PAGE RATHER THAN SLICING AN ARRAY, which
      // is People's note and the same consequence: `total` is the service's
      // count of every unproved address here, so the line under the control is
      // true, and pressing next files another `report_access` row because
      // another page of addresses is another disclosure.
      pager={
        showing && held.page.total > 0 ? (
          <Pager page={page} per={per} setPer={setPer} setPage={setPage} total={held.page.total} unit="addresses" />
        ) : null
      }
    >

      {/* FIRST, BECAUSE IT DECIDES WHAT EVERYTHING UNDER IT MEANS. A table of
          email addresses drawn under an activation's name reads as that
          activation's people unless something on the page says otherwise, and
          the service's own header says as much. This is that something. It is
          also the sentence somebody arriving from People was following. */}
      <section>
        <SecLabel>About this list</SecLabel>
        <div className="panel panel-stack">
          <p>{held.subsetNote}</p>
          <p className="help help-tight">{held.accessNote}</p>
          <div className="acts acts-flush">
            <button type="button" className="btn btn-quiet btn-sm" onClick={onPeople}>
              The people who did register
            </button>
          </div>
        </div>
      </section>

      {held.page.total === 0 ? (
        <NothingHere />
      ) : (
        <>
          <Tally domains={domains} total={held.page.total} order={domainsOrder} />

          <Kept retention={retention} />

          {/* THE WARNING AND THE CONTROL THAT REVEALS THE TABLE ARE THE SAME
              PANEL, which is the arrangement rather than a layout preference.
              There is one way to draw the addresses and it is the button inside
              this box, so the sentence is on the screen before the table exists
              and stays on it while the table is open. */}
          <section>
            <SecLabel>Before viewing addresses</SecLabel>
            <div className="panel panel-stack">
              <p>{held.useNote}</p>
              <p className="help help-tight">
                The addresses are already loaded and this access is recorded. Hiding them only
                protects the screen from passers-by.
              </p>
              <div className="acts acts-flush">
                <button
                  type="button"
                  className="btn btn-quiet btn-sm"
                  aria-expanded={showing}
                  onClick={() => setShowing(!showing)}
                >
                  {showing ? "Hide the addresses" : "Show the addresses"}
                </button>
              </div>
            </div>
          </section>

          {showing ? (
            <Addresses
              attempts={attempts}
              reading={paged.status === "reading"}
              returned={held.page.returned}
              total={held.page.total}
              order={{ sort: addresses.sort, lit: addresses.lit, onSort: sortAddresses }}
            />
          ) : null}
        </>
      )}
    </DeskPage>
  );
}

/**
 * The domain tally, which is the finding.
 *
 * TWO FIGURES AND A TABLE RATHER THAN A TABLE ALONE. The two figures are the
 * only numbers on this screen that a reader would quote at somebody, and both
 * are the service's rather than this console's arithmetic: `total` is the count
 * it sends, and the number of domains is the length of the list it sent. This
 * console adds nothing up. Summing the tally's own columns was the obvious
 * third figure and is not drawn, because the sum is not `total`: an address
 * with no `@` in it is a row the service leaves out of the tally, so a
 * computed sum would sit beside `total` disagreeing with it and neither would
 * carry the explanation.
 */
function Tally({
  domains,
  total,
  order,
}: {
  readonly domains: readonly UnverifiedDomain[];
  readonly total: number;
  /** The tally's order, the service's (`domains_sort`). */
  readonly order: Heads<UnverifiedDomainSortKey>;
}): ReactNode {
  return (
    <section>
      <SecLabel info="These totals cover the whole activation, not only the current page. Repeated domains can reveal a typo printed on the stand.">
        Where the addresses went
      </SecLabel>

      <div className="figs figs-2">
        <div className="fig">
          <span className="fig-l">Unproved addresses</span>
          <span className="fig-v">{total}</span>
          <span className="fig-s">at this activation, not on one page</span>
        </div>
        <div className="fig">
          <span className="fig-l">Domains they were at</span>
          <span className="fig-v">{domains.length}</span>
          <span className="fig-s">counted over the whole activation</span>
        </div>
      </div>

      {domains.length === 0 ? (
        // NOT AN EMPTY TABLE AND NOT A ZERO. There are addresses here, so
        // something is wrong with them rather than nothing being wrong: the
        // tally is computed only over addresses containing an `@`, and a stand
        // with none of those is a stand where the form is taking something that
        // is not an address at all.
        <div className="panel panel-stack" style={{ marginTop: 16 }}>
          <p>
            None of these entries contains an <code>@</code>, so there is no domain to group.
            Review the addresses below.
          </p>
        </div>
      ) : (
        <div className="tablewrap" style={{ marginTop: 16 }}>
          <div className="tablescroll">
            <table className="unverified-domains">
              <thead>
                {/* Every column sorts, and the service sorts the tally where it
                    counts it: UNVERIFIED_DOMAIN_SORT_KEYS. */}
                <tr>
                  <SortHead of="domain" {...order}>
                    Domain
                  </SortHead>
                  <SortHead of="addresses" {...order}>
                    Addresses
                  </SortHead>
                  <SortHead of="codes" {...order}>
                    Codes asked for
                  </SortHead>
                </tr>
              </thead>
              <tbody>
                {domains.map((line) => (
                  <tr key={line.domain}>
                    <td data-label="Domain" className="mono">
                      {line.domain}
                    </td>
                    <td data-label="Addresses">
                      <span className="unverified-count">{line.addresses}</span>
                    </td>
                    <td data-label="Codes asked for" className="num">
                      {line.requests}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

    </section>
  );
}

/**
 * How long these are kept, which is the first fair question when somebody is
 * looking at personal data collected without consent.
 *
 * THE SENTENCE IS THE SERVICE'S AND THE TWO FACTS ARE THE WORKING. The service
 * reads the seal and the client's agreed retention and writes one sentence
 * saying what the pair amounts to, so the sentence is the answer. Both values
 * are drawn beside it because a reader who has to act on this needs to know
 * which clock is missing, not only that one is.
 *
 * THE MISSING-AGREEMENT CASE IS AMBER RATHER THAN QUIET, and it is the same
 * fact the Clients screen already draws that way. Clients.tsx carries a
 * matching paragraph under "No processing agreement on file": no agreement
 * leaves no retention period, the purge reads that column, and without it only
 * sealing the show removes these addresses. Recording the agreement later still
 * reaches the addresses already here. The two screens are two views of one
 * arrangement and must not describe it differently, which is why the wording
 * here is the service's own note plus a pointer at the screen where the period
 * is recorded, rather than a second explanation written from this side.
 */
function Kept({ retention }: { readonly retention: UnverifiedRetention }): ReactNode {
  const noPeriod = retention.retentionMonths === null;

  return (
    <section>
      <SecLabel info="The retention period comes from the client's processing agreement. Set it on the Clients page. A later setting also applies to addresses already here.">
        How long these are kept
      </SecLabel>

      <DefList
        rows={[
          [
            "Show sealed",
            retention.sealedAt === null ? (
              <span className="quiet">not sealed: this activation&rsquo;s record is still open</span>
            ) : (
              when(retention.sealedAt)
            ),
          ],
          [
            "Agreed retention",
            noPeriod ? (
              <span className="tag tag-warn">not agreed</span>
            ) : (
              <>
                {retention.retentionMonths}{" "}
                {retention.retentionMonths === 1 ? "month" : "months"} from when each address was
                first typed
              </>
            ),
          ],
        ]}
      />

      {noPeriod ? (
        <NoticeBox kind="amber" title="No retention period on this client">
          <p>{retention.note}</p>
        </NoticeBox>
      ) : (
        <p className="help help-head">{retention.note}</p>
      )}

    </section>
  );
}

/**
 * The evidence.
 *
 * NO CONTROL ON A ROW AND NO CONTROL OVER THE COLUMN. There is nothing to open,
 * because there is no record behind an address and there must not be one, and
 * there is no way to select, copy or export the column. The operator module
 * names moving these into a mailing tool as the one failure mode this read has,
 * and a console that made it convenient would be the thing that did it.
 *
 * FOUR COLUMNS, AND THE DOMAIN IS NOT ONE OF THEM. It is in the address
 * already, the tally above is the domain view of this table, and a column
 * repeating the second half of every row would be a column somebody sorts by.
 * The one thing the domain field is used for here is the case where it is
 * absent: that row is not counted in the tally, which is a discrepancy a
 * careful reader would otherwise find by adding the tally up, so the row says
 * so itself.
 */
function Addresses({
  attempts,
  reading,
  returned,
  total,
  order,
}: {
  readonly attempts: readonly UnverifiedAttempt[];
  readonly reading: boolean;
  readonly returned: number;
  readonly total: number;
  /** The addresses' order, the service's (`sort`). The pager is the page's slot. */
  readonly order: Heads<UnverifiedSortKey>;
}): ReactNode {
  return (
    <section>
      <SecLabel info="Each page view is recorded. One row is one address, newest first. These addresses are not customers or leads.">
        Addresses
      </SecLabel>

      <ListState
        state={reading ? "reading" : "ready"}
        rows={attempts}
        cols={4}
        unit="addresses"
        empty={
          // A page past the end rather than an activation with nothing on it:
          // the empty state for the second of those is drawn instead of this
          // whole screen, so if this is reached the total is not zero.
          <Empty title="Nothing on this page">
            <p>Go back a page to see the remaining addresses.</p>
          </Empty>
        }
      >
        <>
          <div className="tablewrap">
            <div className="tablescroll">
              <table className="unverified-addresses">
                <thead>
                  {/* Every column sorts, and the service sorts the whole list
                      before it cuts the page: UNVERIFIED_SORT_KEYS. */}
                  <tr>
                    <SortHead of="address" {...order}>
                      Address
                    </SortHead>
                    <SortHead of="first" {...order}>
                      First typed
                    </SortHead>
                    <SortHead of="last" {...order}>
                      Last tried
                    </SortHead>
                    <SortHead of="codes" {...order}>
                      Codes asked for
                    </SortHead>
                  </tr>
                </thead>
                <tbody>
                  {attempts.map((line) => (
                    <tr key={line.attemptId}>
                      <td data-label="Address">
                        <span className="unverified-mailbox">{line.email}</span>
                        {line.domain === null ? (
                          <p className="t-note">
                            No domain in this address, so it is not counted in the tally above.
                          </p>
                        ) : null}
                      </td>
                      <td data-label="First typed" className="num">
                        {when(line.firstRequestedAt)}
                      </td>
                      <td data-label="Last tried" className="num">
                        {when(line.lastRequestedAt)}
                      </td>
                      <td data-label="Codes asked for" className="num">
                        {line.requestCount}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      </ListState>

      <p className="help help-head">
        This page holds {returned} of {total} {total === 1 ? "address" : "addresses"}.
      </p>
    </section>
  );
}

/**
 * An activation where nothing was typed and left unproved.
 *
 * A COUNT OF ZERO IS A FACT WORTH KEEPING AND IS ALSO TWO DIFFERENT FACTS,
 * which is migration 130's own observation and the reason this is a sentence
 * rather than an empty table. It is what a stand where everybody who started
 * finished looks like, and it is what a stand nobody typed anything at looks
 * like, and nothing in this table tells them apart. Drawing it as a clean zero
 * under a heading about drop-off would report the first of those.
 */
function NothingHere(): ReactNode {
  return (
    <Empty title="No unverified emails">
      <p>
        Either every email was verified or nobody requested a code. This page cannot tell which.
      </p>
    </Empty>
  );
}

/**
 * A timestamp in the reader's own zone, or the fact that none was sent.
 *
 * An unparseable string is drawn as what the service said rather than as the
 * word for nothing, which is People's rule and Ride.tsx's before it: the
 * service did say something, and whoever has to work out why wants to see it.
 */
function when(value: string | null): ReactNode {
  if (value === null || value === "") return <span className="quiet">not recorded</span>;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
}
